import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCurrentUser } from '@agro1desenvolvimento/react-hooks';
import {
  Card, Dropdown, Calendar, InputNumber, InputText, Checkbox,
} from '@agro1desenvolvimento/react-components';
import { isNil } from 'lodash';
import { CanteiroAreiaAnalise } from '../../../../../../services/sementes/canteiroAreia';

interface Analista {
  id: string;
  nome: string;
}

interface Props {
  analistas: Analista[];
  searchAnalistas: (query: string) => Promise<Analista[]>;
  onSubmit: (id: string, dados: CanteiroAreiaAnalise, concluir?: boolean) => Promise<void>;
  loading: boolean;
  initialValues?: CanteiroAreiaAnalise;
  disabled?: boolean;
}

const dataInicial = new Date();

export default function FormularioCanteiroAreiaAnalise({
  analistas, searchAnalistas, onSubmit, loading, initialValues, disabled = false,
}: Props) {
  const { id } = useParams<{ id: string }>();
  const currentUser = useCurrentUser();
  const [analistasOptions, setAnalistasOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const analistaIdPadrao = useMemo(() => {
    if (!currentUser || analistas.length === 0) return '';

    const usuarioEncontrado = analistas.find((a) => a.id === currentUser.id);
    return usuarioEncontrado ? usuarioEncontrado.id : '';
  }, [analistas, currentUser]);

  useEffect(() => {
    setAnalistasOptions(analistas.map((a) => ({ label: a.nome, value: a.id })));
  }, [analistas]);

  const [formData, setFormData] = useState({
    analista: initialValues?.analista || analistaIdPadrao,
    data: initialValues?.data || dataInicial as Date | undefined,
    amostra1: initialValues?.amostra1 || 0,
    amostra2: initialValues?.amostra2 || 0,
    media: initialValues?.media || 0,
    observacao: initialValues?.observacao || '',
    gerarBasRir: isNil(initialValues?.gerarBasRir) ? false : initialValues!.gerarBasRir,
  });
  const [submitting, setSubmitting] = useState(false);

  // Atualizar formData quando initialValues mudar (dados da API)
  useEffect(() => {
    if (initialValues) {
      setFormData({
        analista: initialValues.analista || analistaIdPadrao,
        data: initialValues.data || dataInicial,
        amostra1: initialValues.amostra1 || 0,
        amostra2: initialValues.amostra2 || 0,
        media: initialValues.media || 0,
        observacao: initialValues.observacao || '',
        gerarBasRir: isNil(initialValues.gerarBasRir) ? false : initialValues!.gerarBasRir,
      });
    }
  }, [initialValues, analistaIdPadrao]);

  // Verificar se houve alteração nos dados
  const hasChanges = useMemo(() => {
    if (!initialValues) return true; // Se não tem valores iniciais, considera que tem alteração

    // Comparar datas de forma mais segura
    const dataChanged = formData.data && initialValues.data
      ? formData.data.getTime() !== initialValues.data.getTime()
      : formData.data !== initialValues.data;

    return (
      formData.analista !== initialValues.analista
      || dataChanged
      || formData.amostra1 !== initialValues.amostra1
      || formData.amostra2 !== initialValues.amostra2
      || formData.media !== initialValues.media
      || formData.observacao !== initialValues.observacao
      || formData.gerarBasRir !== initialValues.gerarBasRir
    );
  }, [formData, initialValues]);

  useEffect(() => {
    if (analistaIdPadrao && !formData.analista) {
      setFormData((prev) => ({ ...prev, analista: analistaIdPadrao }));
    }
  }, [analistaIdPadrao, formData?.analista]);

  useEffect(() => {
    if (formData.amostra1 > 0 && formData.amostra2 > 0) {
      const mediaCalculada = (formData.amostra1 + formData.amostra2) / 2;
      setFormData((prev) => ({ ...prev, media: mediaCalculada }));
    }
  }, [formData?.amostra1, formData?.amostra2]);

  const handleFilterAnalistas = async (event: { filter: string }) => {
    const query = event.filter;

    if (!query || query.trim().length === 0) {
      setAnalistasOptions(
        analistas.map((analista) => ({ label: analista.nome, value: analista.id })),
      );
      return;
    }

    try {
      const resultados = await searchAnalistas(query);
      setAnalistasOptions(
        resultados.map((analistasResultados) => (
          { label: analistasResultados.nome, value: analistasResultados.id })),
      );
    } catch (err) {
      console.error('Erro ao buscar analistas:', err);
    }
  };

  const isFormValid = useMemo(() => formData.analista
      && formData.data
      && formData.amostra1 > 0
      && formData.amostra2 > 0
      && formData.media > 0, [formData]);

  const handleSubmit = async (e: React.FormEvent, concluir = false) => {
    e.preventDefault();

    if (!isFormValid || !id) return;

    try {
      setSubmitting(true);

      await onSubmit(id, {
        analista: formData.analista,
        data: formData.data!,
        amostra1: formData.amostra1,
        amostra2: formData.amostra2,
        media: formData.media,
        observacao: formData.observacao,
        gerarBasRir: formData.gerarBasRir,
      }, concluir);
    } catch (error) {
      console.error('Erro ao submeter análise:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Bloqueia submit com Enter - apenas botões podem submeter
  };

  const isSumbitDisabled = !isFormValid || loading || submitting;

  return (
    <Card className="formulario-canteiro-areia-analise">
      <div className="header">
        <h3 className="title">Canteiro de Areia</h3>
        <p className="subtitle">Complete os campos abaixo para concluir a análise do canteiro de areia</p>
      </div>

      <form onSubmit={handleFormSubmit} className="form" style={disabled ? { opacity: 0.6, pointerEvents: 'none' } : {}}>
        <div className="form-field">
          <label>Analista</label>
          <Dropdown
            value={formData.analista}
            options={analistasOptions}
            onChange={(e) => setFormData({ ...formData, analista: e.value })}
            placeholder="Selecione..."
            filter
            filterPlaceholder="Buscar analista..."
            onFilter={handleFilterAnalistas}
            disabled={disabled}
          />
        </div>

        <div className="form-field">
          <label>Data</label>
          <Calendar
            value={formData.data}
            onChange={(e) => setFormData({ ...formData, data: e.value as Date })}
            dateFormat="dd/mm/yy"
            disabled={disabled}
          />
        </div>

        <div className="form-field checkbox-field">
          <div className="checkbox-wrapper">
            <Checkbox
              checked={formData.gerarBasRir}
              onChange={(e) => setFormData({ ...formData, gerarBasRir: e.checked || false })}
              disabled={disabled}
            />
            <label className="checkbox-label">Gerar BAS/RIR</label>
          </div>
        </div>

        <div className="form-field">
          <label>Amostra 1</label>
          <InputNumber
            value={formData.amostra1}
            onValueChange={(e) => setFormData({ ...formData, amostra1: e.value || 0 })}
            min={0}
            max={100}
            minFractionDigits={0}
            maxFractionDigits={1}
            disabled={disabled}
          />
        </div>

        <div className="form-field">
          <label>Amostra 2</label>
          <InputNumber
            value={formData.amostra2}
            onValueChange={(e) => setFormData({ ...formData, amostra2: e.value || 0 })}
            min={0}
            max={100}
            minFractionDigits={0}
            maxFractionDigits={1}
            disabled={disabled}
          />
        </div>

        <div className="form-field">
          <label>Média</label>
          <InputNumber
            value={formData.media}
            onValueChange={(e) => setFormData({ ...formData, media: e.value || 0 })}
            min={0}
            max={100}
            minFractionDigits={1}
            maxFractionDigits={1}
            disabled={disabled}
          />
        </div>

        <div className="form-field observacao-field">
          <label>Observação (opcional)</label>
          <InputText
            value={formData.observacao}
            onChange={(e) => setFormData({ ...formData, observacao: e.target.value })}
            placeholder="Digite suas observações sobre a análise"
            disabled={disabled}
          />
        </div>

        {!disabled && (
          <div className="form-field submit-field">
            {hasChanges ? (
              <button
                type="button"
                className={`submit-button ${isFormValid ? 'valid' : 'invalid'}`}
                disabled={isSumbitDisabled}
                onClick={(e) => handleSubmit(e, false)}
              >
                {(loading || submitting) ? 'Salvando...' : 'Salvar'}
              </button>
            ) : (
              <button
                type="submit"
                className={`submit-button ${isFormValid ? 'valid' : 'invalid'}`}
                disabled={isSumbitDisabled}
                onClick={(e) => handleSubmit(e, true)}
              >
                {(loading || submitting) ? 'Concluindo...' : 'Concluir Análise'}
              </button>
            )}
          </div>
        )}
      </form>
    </Card>
  );
}
